import { formatPayPeriod } from '../../models/pending-timesheets.model';
import TimesheetComment from '../timesheet-comment.component';
import ApprovedTimesheetDetails from '../approved-timesheet-details.component';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import classnames from 'classnames';
import AppModal from '../../../../components/app-modal.component';
import { IOnlineApprovalTimeEntry } from '../../models/online-approval-time-entry.model';
import TimesheetEditEntry from './timesheet-edit-entry.component';
import { ReactComponent as PlusIcon } from '../../../../images/plus.svg';
import classNames from 'classnames';
import React, { ChangeEvent } from 'react';
import { ITimesheetEditModalState } from './timesheet-edit-modal.component';

const renderDesktop = (props: ITimesheetEditModalState): JSX.Element => {
    const { supervisorComments, selectedTimesheet, isSaveDisabled, showCommentError } = props;

    const renderHeader = (): JSX.Element => {
        return (
            <span>
                <span className="sr-only large-text">Timesheet for </span>
                {selectedTimesheet?.travelerFirstName} {selectedTimesheet?.travelerLastName}{' '}
                <span className="sr-only large-text"> Payperiod </span>
                <span className="font-weight-normal">
                    {formatPayPeriod(props.payPeriodStart)} - {formatPayPeriod(props.payPeriodEnd)}
                </span>
                <br />
                <span className="timeentry-comment font-weight-normal">
                    Be sure to enter hours in 24-hour time (1 PM = 1300)
                </span>
            </span>
        );
    };

    const renderFooter = (): JSX.Element => {
        return (
            <>
                <TimesheetComment timesheetComment={selectedTimesheet?.timesheetComment} textAlign={'text-left'} />
                <ApprovedTimesheetDetails
                    approvedDate={selectedTimesheet?.onlineApprovalDate}
                    supervisorComments={supervisorComments}
                    textAlign={'text-left'}
                />
                <Row className="w-100">
                    <Col>
                        <Row className="font-weight-bold mb-2">
                            <Col>Total Hours: </Col>
                            <Col>{selectedTimesheet?.totalHours?.toFixed(2)}</Col>
                        </Row>
                        {selectedTimesheet && selectedTimesheet.regularHours > 0 && (
                            <Row>
                                <Col>Regular: </Col>
                                <Col>{selectedTimesheet?.regularHours?.toFixed(2)}</Col>
                            </Row>
                        )}
                        {selectedTimesheet && selectedTimesheet.onCallHours > 0 && (
                            <Row>
                                <Col>On Call: </Col>
                                <Col>{selectedTimesheet?.onCallHours?.toFixed(2)}</Col>
                            </Row>
                        )}
                        {selectedTimesheet && selectedTimesheet.callbackHours > 0 && (
                            <Row>
                                <Col>Callback: </Col>
                                <Col>{selectedTimesheet?.callbackHours?.toFixed(2)}</Col>
                            </Row>
                        )}
                        {selectedTimesheet && selectedTimesheet.chargeHours > 0 && (
                            <Row>
                                <Col>Charge: </Col>
                                <Col>{selectedTimesheet?.chargeHours?.toFixed(2)}</Col>
                            </Row>
                        )}
                    </Col>
                    <Col className="align-items-end d-flex">
                        <Button color="light" className="rounded-pill mr-2 ml-auto" onClick={props.close}>
                            Cancel
                        </Button>
                        {!selectedTimesheet.onlineApprovalDate && (
                            <>
                                <Button
                                    id="save-button"
                                    color={isSaveDisabled ? 'light' : 'primary'}
                                    className={classnames(['rounded-pill', { 'text-muted': isSaveDisabled }])}
                                    onClick={() => {
                                        props.save(selectedTimesheet, supervisorComments);
                                    }}
                                    disabled={isSaveDisabled}
                                >
                                    Save & Close
                                </Button>
                            </>
                        )}
                    </Col>
                </Row>
            </>
        );
    };

    return (
        <AppModal
            header={renderHeader()}
            footer={renderFooter()}
            isOpen={props.isOpen}
            close={props.close}
            className={'modal--fluid'}
        >
            <Table borderless size="sm">
                <caption className="sr-only">Edit time entries</caption>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th className="text-nowrap">Shift In</th>
                        <th className="text-nowrap">Break Out</th>
                        <th className="text-nowrap">Break In</th>
                        <th className="text-nowrap">Shift Out</th>
                        {props.timesheetPackage?.timesheet?.showCostCenters && (
                            <th className="text-nowrap">Cost Center</th>
                        )}
                        <th>Hours</th>
                    </tr>
                </thead>
                <tbody>
                    {props.isLoading ? (
                        <tr>
                            <td>Loading...</td>
                        </tr>
                    ) : (
                        props.selectedTimesheetTimeEntries
                            ?.filter(
                                (te: IOnlineApprovalTimeEntry) => te.timesheetId === selectedTimesheet?.timesheetId,
                            )
                            .map((timeEntry: IOnlineApprovalTimeEntry, i: number) => (
                                <TimesheetEditEntry
                                    key={timeEntry.id + i}
                                    entryKey={timeEntry.id + i}
                                    timeEntry={timeEntry}
                                    timesheetPackage={props.timesheetPackage}
                                />
                            ))
                    )}
                </tbody>
            </Table>

            {props.showAddDelete && (
                <div className="add-container">
                    <Button
                        color="link"
                        className="add-button font-weight-600"
                        onClick={props.addShift}
                        id="add-shift-button"
                    >
                        <PlusIcon />
                        &nbsp; Add Shift
                    </Button>
                </div>
            )}

            {!selectedTimesheet.onlineApprovalDate && (
                <>
                    <label htmlFor="supervisorComment" className="sr-only large-text">
                        Supervisor Comments (required)
                    </label>
                    <Input
                        className={classNames([
                            'timeentry-comment',
                            {
                                'error-highlight': showCommentError,
                            },
                        ])}
                        disabled={!!selectedTimesheet.onlineApprovalDate}
                        type="textarea"
                        name="supervisorComment"
                        id="supervisorComment"
                        placeholder="comment required to edit timesheet"
                        value={supervisorComments}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => props.setSupervisorComments(e?.target?.value)}
                    />
                </>
            )}
        </AppModal>
    );
};

export default renderDesktop;
